import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Modal,
  Form,
  Alert,
  Spinner,
  Accordion,
  Badge,
} from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import CommitHistory from './CommitHistory';

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const BranchManagement = ({ token }) => {
  console.log(
    'BranchManagement component mounted with token:',
    token ? 'present' : 'missing'
  );
  const { repoName } = useParams();
  console.log('Repository name from URL params:', repoName);

  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showProtectionModal, setShowProtectionModal] =
    useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [branchToDelete, setBranchToDelete] = useState(null);
  const [newBranchName, setNewBranchName] = useState('');
  const [sourceBranch, setSourceBranch] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [protectionRules, setProtectionRules] = useState({
    require_pull_request: true,
    require_code_owner_reviews: false,
    required_approving_review_count: 1,
    dismiss_stale_reviews: true,
    enforce_admins: true,
  });

  const fetchBranches = useCallback(async () => {
    try {
      console.log('Starting fetchBranches...');
      setLoading(true);
      setError(null);

      if (!token) {
        console.error('No token available');
        navigate('/login');
        return;
      }

      if (!repoName) {
        console.error('No repository name available');
        setError('Repository name is missing');
        setLoading(false);
        return;
      }

      console.log('Fetching branches for repo:', repoName);
      console.log('Using token:', token ? 'Present' : 'Missing');

      const response = await axios.get(
        `${BACKEND_URL}/api/repositories/${repoName}/branches`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }
      );

      console.log('Received branches response:', response.data);

      if (!Array.isArray(response.data)) {
        console.error('Invalid response format:', response.data);
        setError('Invalid response format from server');
        setBranches([]);
      } else {
        setBranches(response.data);
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching branches:', err);
      console.error('Error response:', err.response?.data);
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        'Failed to fetch branches';
      setError(errorMessage);
      setBranches([]);

      if (err.response?.status === 401) {
        console.log('Unauthorized access, redirecting to login...');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [repoName, token, navigate]);

  useEffect(() => {
    console.log(
      'useEffect triggered with repoName:',
      repoName,
      'token:',
      token ? 'present' : 'missing'
    );
    if (repoName && token) {
      console.log(
        'Initializing branch management for repo:',
        repoName
      );
      fetchBranches();
    } else {
      console.log('Missing required props:', {
        hasRepoName: !!repoName,
        hasToken: !!token,
      });
      if (!token) {
        navigate('/login');
      }
    }
  }, [repoName, token, fetchBranches, navigate]);

  const handleCreateBranch = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${BACKEND_URL}/api/repositories/${repoName}/branches`,
        {
          name: newBranchName,
          source_branch: sourceBranch,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setShowCreateModal(false);
      setNewBranchName('');
      setSourceBranch('');
      fetchBranches();
      setError(null);
    } catch (err) {
      setError(
        err.response?.data?.error || 'Failed to create branch'
      );
    }
  };

  const handleDeleteClick = (branch) => {
    if (branch.isDefault) {
      setError(
        'Cannot delete the default branch. Please set another branch as default first.'
      );
      return;
    }
    setBranchToDelete(branch);
    setShowDeleteModal(true);
  };

  const handleDeleteBranch = async () => {
    try {
      if (!branchToDelete) return;
      await axios.delete(
        `${BACKEND_URL}/api/repositories/${repoName}/branches/${branchToDelete.name}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setShowDeleteModal(false);
      setBranchToDelete(null);
      fetchBranches();
      setError(null);
    } catch (err) {
      setError(
        err.response?.data?.error || 'Failed to delete branch'
      );
    }
  };

  const handleUpdateProtection = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${BACKEND_URL}/api/repositories/${repoName}/branches/${selectedBranch}/protection`,
        protectionRules,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setShowProtectionModal(false);
      fetchBranches();
      setError(null);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          'Failed to update protection rules'
      );
    }
  };

  const handleProtectionClick = (branch) => {
    setSelectedBranch(branch.name);
    setProtectionRules(
      branch.protection_rules || {
        require_pull_request: true,
        require_code_owner_reviews: false,
        required_approving_review_count: 1,
        dismiss_stale_reviews: true,
        enforce_admins: true,
      }
    );
    setShowProtectionModal(true);
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading branches...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="m-3">
        <Alert.Heading>Error Loading Branches</Alert.Heading>
        <p>{error}</p>
        <Button variant="outline-danger" onClick={fetchBranches}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Branch Management for {repoName}</h2>
        <Button variant="secondary" onClick={() => navigate(-1)}>
          Back to Repositories
        </Button>
      </div>

      <Button
        variant="primary"
        onClick={() => setShowCreateModal(true)}
        className="mb-3"
      >
        Create New Branch
      </Button>

      <Accordion>
        {Array.isArray(branches) &&
          branches.map((branch) => (
            <Accordion.Item key={branch.name} eventKey={branch.name}>
              <Accordion.Header>
                <div className="d-flex justify-content-between align-items-center w-100 me-3">
                  <span>
                    {branch.name}
                    {branch.isDefault && (
                      <Badge bg="info" className="ms-2">
                        Default Branch
                      </Badge>
                    )}
                  </span>
                  <div>
                    {branch.protected ? (
                      <Badge bg="success" className="me-2">
                        Protected
                      </Badge>
                    ) : (
                      <Badge bg="warning" className="me-2">
                        Not Protected
                      </Badge>
                    )}
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <div className="mb-3">
                  <div className="d-flex gap-2 mb-3">
                    <Button
                      variant="info"
                      size="sm"
                      onClick={() => handleProtectionClick(branch)}
                    >
                      Protection Rules
                    </Button>
                  </div>

                  <CommitHistory
                    repoName={repoName}
                    branchName={branch.name}
                    accessToken={token}
                  />
                </div>
              </Accordion.Body>
            </Accordion.Item>
          ))}
      </Accordion>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Branch Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {branchToDelete && (
            <>
              <p>
                Are you sure you want to delete branch "
                {branchToDelete.name}"?
              </p>
              <Alert variant="warning">
                This action cannot be undone. All commits unique to
                this branch will be lost if they haven't been merged.
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowDeleteModal(false);
              setBranchToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteBranch}
            disabled={branchToDelete?.isDefault}
          >
            Delete Branch
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create Branch Modal */}
      <Modal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Create New Branch</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCreateBranch}>
            <Form.Group className="mb-3">
              <Form.Label>Branch Name</Form.Label>
              <Form.Control
                type="text"
                value={newBranchName}
                onChange={(e) => setNewBranchName(e.target.value)}
                placeholder="feature/new-feature"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Source Branch</Form.Label>
              <Form.Select
                value={sourceBranch}
                onChange={(e) => setSourceBranch(e.target.value)}
                required
              >
                <option value="">Select source branch</option>
                {branches.map((branch) => (
                  <option key={branch.name} value={branch.name}>
                    {branch.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Button variant="primary" type="submit">
              Create Branch
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Protection Modal */}
      <Modal
        show={showProtectionModal}
        onHide={() => setShowProtectionModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Branch Protection Rules</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleUpdateProtection}>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Require pull request reviews"
                checked={protectionRules.require_pull_request}
                onChange={(e) =>
                  setProtectionRules({
                    ...protectionRules,
                    require_pull_request: e.target.checked,
                  })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Require code owner reviews"
                checked={protectionRules.require_code_owner_reviews}
                onChange={(e) =>
                  setProtectionRules({
                    ...protectionRules,
                    require_code_owner_reviews: e.target.checked,
                  })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Required approving reviews</Form.Label>
              <Form.Control
                type="number"
                min="1"
                max="6"
                value={
                  protectionRules.required_approving_review_count
                }
                onChange={(e) =>
                  setProtectionRules({
                    ...protectionRules,
                    required_approving_review_count: parseInt(
                      e.target.value
                    ),
                  })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Dismiss stale pull request approvals"
                checked={protectionRules.dismiss_stale_reviews}
                onChange={(e) =>
                  setProtectionRules({
                    ...protectionRules,
                    dismiss_stale_reviews: e.target.checked,
                  })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Include administrators"
                checked={protectionRules.enforce_admins}
                onChange={(e) =>
                  setProtectionRules({
                    ...protectionRules,
                    enforce_admins: e.target.checked,
                  })
                }
              />
            </Form.Group>
            <Button variant="primary" type="submit">
              Update Protection Rules
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default BranchManagement;
